import _ from 'lodash';
import { useMemo, useState } from 'react';
import { KPI_TAG_METADATA, WarehouseKPI } from '../../dashboard/kpi';
import { ActualityAggregatedStatsProps } from '../components/ActualityAggregatedStats';
import useActualityAggregationMetricDescriptors from './useActualityAggregationMetricDescriptors';

export const useActualityAggregatedStats = (
  props: ActualityAggregatedStatsProps,
) => {
  const {
    data,
    fields,
    mode = 'all',
    defaultFieldsLimit = 5,
    defaultFields,
    summaryFields,
  } = props;

  const [showAll, setShowAll] = useState(false);

  const aggMetricDescriptors = useActualityAggregationMetricDescriptors();
  const groupedDescriptors = useMemo(
    () => _.groupBy(_.values(aggMetricDescriptors), 'category'),
    [aggMetricDescriptors],
  );

  const getFieldsToShow = (categoryDescriptors: WarehouseKPI[]) => {
    switch (mode) {
      case 'fieldsOnly':
        return _.filter(
          categoryDescriptors,
          d => fields && _.includes(fields, d.id),
        );

      case 'summary':
        if (showAll) {
          return fields
            ? _.filter(categoryDescriptors, d => _.includes(fields, d.id))
            : categoryDescriptors;
        } else {
          const fieldsToUse = summaryFields || defaultFields;
          return fieldsToUse
            ? _.filter(categoryDescriptors, d => _.includes(fieldsToUse, d.id))
            : _.take(categoryDescriptors, defaultFieldsLimit);
        }

      case 'loadMore':
        let availableFields: WarehouseKPI[];

        if (fields) {
          availableFields = _.filter(categoryDescriptors, d =>
            _.includes(fields, d.id),
          );
        } else if (defaultFields) {
          availableFields = _.filter(categoryDescriptors, d =>
            _.includes(defaultFields, d.id),
          );
        } else {
          availableFields = categoryDescriptors;
        }

        return showAll
          ? availableFields
          : _.take(availableFields, defaultFieldsLimit);

      case 'all':
      default:
        return fields
          ? _.filter(categoryDescriptors, d => _.includes(fields, d.id))
          : categoryDescriptors;
    }
  };

  const hasCategoryStats = mode !== 'fieldsOnly' && _.isEmpty(fields);

  const getSummaryFieldsCount = () => {
    return _.sum(
      _.map(KPI_TAG_METADATA, (categoryDescriptor, categoryId) => {
        const categoryDescriptors = groupedDescriptors[categoryId] || [];
        const fieldsToUse = summaryFields || defaultFields;

        if (fieldsToUse) {
          return _.filter(categoryDescriptors, d =>
            _.includes(fieldsToUse, d.id),
          ).length;
        } else {
          return Math.min(categoryDescriptors.length, defaultFieldsLimit);
        }
      }),
    );
  };

  const getTotalAvailableFields = () => {
    return _.sum(
      _.map(KPI_TAG_METADATA, (categoryDescriptor, categoryId) => {
        const categoryDescriptors = groupedDescriptors[categoryId] || [];

        if (mode === 'summary') {
          return fields
            ? _.filter(categoryDescriptors, d => _.includes(fields, d.id))
                .length
            : categoryDescriptors.length;
        }

        if (fields) {
          return _.filter(categoryDescriptors, d => _.includes(fields, d.id))
            .length;
        } else if (defaultFields) {
          return _.filter(categoryDescriptors, d =>
            _.includes(defaultFields, d.id),
          ).length;
        } else {
          return categoryDescriptors.length;
        }
      }),
    );
  };

  const getCurrentlyShowingFields = () => {
    return _.sum(
      _.map(KPI_TAG_METADATA, (categoryDescriptor, categoryId) => {
        const categoryDescriptors = groupedDescriptors[categoryId] || [];
        return getFieldsToShow(categoryDescriptors).length;
      }),
    );
  };

  const shouldShowLoadMore =
    mode === 'loadMore' &&
    !showAll &&
    getCurrentlyShowingFields() < getTotalAvailableFields();

  const shouldShowLess =
    mode === 'loadMore' &&
    showAll &&
    getTotalAvailableFields() > defaultFieldsLimit;

  const shouldShowSummaryToggle =
    mode === 'summary' && getSummaryFieldsCount() < getTotalAvailableFields();

  const fieldsToShowByCategory = useMemo(
    () =>
      _.map(KPI_TAG_METADATA, (categoryDescriptor, categoryId) => {
        const categoryDescriptors: WarehouseKPI[] =
          groupedDescriptors[categoryId] || [];
        const fieldsToShow = getFieldsToShow(categoryDescriptors);
        return {
          categoryId,
          categoryDescriptor,
          fieldsToShow,
        };
      }).filter(c => !_.isEmpty(c.fieldsToShow)),
    [
      groupedDescriptors,
      showAll,
      mode,
      fields,
      summaryFields,
      defaultFields,
      defaultFieldsLimit,
    ],
  );

  return {
    showAll,
    setShowAll,
    shouldShowLoadMore,
    shouldShowLess,
    getCurrentlyShowingFields,
    getTotalAvailableFields,
    shouldShowSummaryToggle,
    hasCategoryStats,
    fieldsToShowByCategory,
  };
};
